/**
 * "Regenerate section" / "Fill this section" for generated-document fields
 * (`data-field-id`, CONTRACTS §5). Builds a fill-only prompt from the field's template
 * instruction, the section heading, the current content and the project's requirements, asks
 * the AI for replacement HTML (task 'doc.rewrite', profile 'fill', JSON schema, stable seed)
 * and returns it for the editor's `fill_field` edit-plan op, which shows it as a review.
 */
import { supabase } from '../../lib/supabase';
import aiService from '../../services/aiService';
import { AI_TASKS, stableSeed } from '../../constants/aiModels';
import { parseTemplateFields } from '../../utils/templateFields';
import { PAGE_BREAK_HTML } from '../../utils/pageBreakHtml';

const NEEDS_INPUT_RE = /\[Needs input:\s*([^\]]+)\]/i;
const MAX_REQUIREMENTS = 60;
const MAX_CONTEXT_CHARS = 4000;

const FIELD_FILL_SCHEMA = {
  type: 'object',
  properties: {
    html: { type: 'string', description: 'Replacement HTML for the section only (no surrounding heading).' },
  },
  required: ['html'],
  propertyOrdering: ['html'],
};

const SYSTEM_INSTRUCTION = `You rewrite ONE section of a generated business document.
Return JSON {"html": "..."} where html is the new content for that section only.
Rules:
- Use only facts from the REQUIREMENTS and the CURRENT CONTENT. Do not invent systems, numbers, names or dates.
- If the requirements do not contain enough information, write "[Needs input: <what is missing>]" instead of guessing.
- Follow the SECTION INSTRUCTION, output kind and word limit.
- Block sections: semantic HTML (p, ul, ol, li, table, strong, em). Inline sections: plain text or inline HTML only (no block tags).
- Never repeat the section heading. No markdown, no code fences, no data-* attributes.`;

/**
 * Trim text to a maximum length.
 * @param {string} text - Text.
 * @param {number} max - Max characters.
 * @returns {string} Trimmed text.
 */
const clip = (text, max) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * Describe a generated field in document HTML.
 * @param {string} html - Document HTML.
 * @param {string} fieldId - `data-field-id` value.
 * @returns {{fieldId: string, isBlock: boolean, status: string|null, currentHtml: string, currentText: string, heading: string, needsInput: string|null}|null} Field description, or null when not found.
 */
export function describeGeneratedField(html, fieldId) {
  if (!html || !fieldId || typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const element = Array.from(doc.body.querySelectorAll('[data-field-id]'))
    .find((el) => el.getAttribute('data-field-id') === fieldId);
  if (!element) return null;

  // Nearest heading before the field's top-level block.
  let top = element;
  while (top.parentElement && top.parentElement !== doc.body) top = top.parentElement;
  let heading = '';
  for (let node = top.previousElementSibling; node; node = node.previousElementSibling) {
    if (/^H[1-6]$/.test(node.tagName)) {
      heading = node.textContent || '';
      break;
    }
  }
  // A top-level field split by page breaks (wrapper, break, wrapper, ...) is one field: its
  // current content is every piece, with the breaks kept between them.
  let currentHtml = element.innerHTML;
  let currentText = element.textContent || '';
  if (element.parentElement === doc.body && element.tagName !== 'SPAN') {
    let pending = '';
    for (let node = element.nextElementSibling; node; node = node.nextElementSibling) {
      if (node.hasAttribute('data-page-break') && node.getAttribute('data-page-break') !== 'false') {
        pending += PAGE_BREAK_HTML;
      } else if (node.getAttribute('data-field-id') === fieldId && node.tagName !== 'SPAN') {
        currentHtml += pending + node.innerHTML;
        currentText += ` ${node.textContent || ''}`;
        pending = '';
      } else {
        break;
      }
    }
  }
  const needs = NEEDS_INPUT_RE.exec(currentText);
  return {
    fieldId,
    isBlock: element.tagName !== 'SPAN',
    status: element.getAttribute('data-field-status'),
    currentHtml,
    currentText,
    heading: clip(heading, 200),
    needsInput: needs ? clip(needs[1], 400) : null,
  };
}

/**
 * Load the template field definition (instruction, kind, word limit) for a field id.
 * @param {string|null} templateId - Template id of the document.
 * @param {string} fieldId - Field id.
 * @returns {Promise<{instruction: string, kind: string, maxWords: number|null, source: string}|null>} Field, or null.
 */
export async function loadTemplateField(templateId, fieldId) {
  if (!templateId) return null;
  try {
    const { data, error } = await supabase.from('templates').select('content').eq('id', templateId).maybeSingle();
    if (error || !data?.content) return null;
    const field = parseTemplateFields(data.content).fields.find((f) => f.id === fieldId);
    return field ? { instruction: field.instruction, kind: field.kind, maxWords: field.maxWords, source: field.source } : null;
  } catch {
    return null;
  }
}

/**
 * Load the project's requirements as numbered evidence lines.
 * @param {string|null} projectId - Project id.
 * @returns {Promise<string[]>} Lines such as `R1. Title (High): description`.
 */
async function loadRequirementLines(projectId) {
  if (!projectId) return [];
  try {
    const { data, error } = await supabase
      .from('requirements')
      .select('title, description, priority, type')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true })
      .limit(MAX_REQUIREMENTS);
    if (error || !Array.isArray(data)) return [];
    return data.map((r, i) => `R${i + 1}. ${clip(r.title, 160)}${r.priority ? ` (${r.priority})` : ''}: ${clip(r.description, 600)}`);
  } catch {
    return [];
  }
}

/**
 * Generate replacement content for one generated field.
 * @param {object} input - Input.
 * @param {string} input.html - Current document HTML.
 * @param {string} input.fieldId - Field id.
 * @param {string|null} [input.documentId] - Document id (seed).
 * @param {string} [input.documentTitle] - Document title.
 * @param {string|null} [input.templateId] - Template the document was generated from.
 * @param {string|null} [input.projectId] - Project id (requirements).
 * @param {number} [input.variant=0] - Incremented for each regeneration of the same field.
 * @param {AbortSignal} [input.signal] - Abort signal.
 * @returns {Promise<{html: string, isBlock: boolean}>} Replacement HTML.
 * @throws {Error} When the field is missing or the AI returns nothing usable.
 */
export async function regenerateFieldContent({
  html,
  fieldId,
  documentId = null,
  documentTitle = '',
  templateId = null,
  projectId = null,
  variant = 0,
  signal,
}) {
  const field = describeGeneratedField(html, fieldId);
  if (!field) throw new Error('This section could not be found in the document.');
  const [templateField, requirements] = await Promise.all([
    loadTemplateField(templateId, fieldId),
    loadRequirementLines(projectId),
  ]);
  const instruction = templateField?.instruction || field.needsInput || (field.heading ? `Write the "${field.heading}" section.` : 'Improve this section.');
  const kind = templateField?.kind || (field.isBlock ? 'rich' : 'text');
  const maxWords = templateField?.maxWords || null;

  const prompt = [
    `DOCUMENT: ${clip(documentTitle, 200) || 'Untitled document'}`,
    field.heading ? `SECTION HEADING: ${field.heading}` : null,
    `SECTION INSTRUCTION: ${instruction}`,
    `OUTPUT: ${field.isBlock ? 'block HTML' : 'inline text'}; kind=${kind}${maxWords ? `; at most ${maxWords} words` : ''}`,
    `CURRENT CONTENT:\n${clip(field.currentText, MAX_CONTEXT_CHARS) || '(empty)'}`,
    requirements.length
      ? `REQUIREMENTS:\n${requirements.join('\n')}`
      : 'REQUIREMENTS: (none available; rely on the current content and mark gaps with [Needs input: ...])',
  ].filter(Boolean).join('\n\n');

  const result = await aiService.runTask({
    task: AI_TASKS.DOC_REWRITE,
    profile: 'fill',
    seed: stableSeed('doc.field', documentId || '', fieldId, instruction, variant),
    systemInstruction: SYSTEM_INSTRUCTION,
    prompt,
    responseSchema: FIELD_FILL_SCHEMA,
    signal,
  });
  const out = typeof result?.json?.html === 'string' ? result.json.html.trim() : '';
  if (!out) throw new Error('The AI returned no content for this section.');
  return { html: out, isBlock: field.isBlock };
}
